// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type Address = {
  line1: string;
  line2?: string | null | undefined;
  city: string;
  state: string;
  postcode: string;
  country: string;
};
export const AddressSchema = z.object({
  line1: z.string().min(1),
  line2: z.string().nullable().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  postcode: z.string().min(1),
  country: z.string().default("Australia"),
}).strict();

export type Property = {
  _id: string;
  organizationId: string;
  name: string;
  referenceCode?: string | null | undefined;
  address: Address;
  metadata?: Record<string, unknown> | undefined;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};
export const PropertySchema = z.object({
  _id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  referenceCode: z.string().nullable().optional(),
  address: AddressSchema,
  metadata: z.record(z.unknown()).optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).strict();

export type CreatePropertyBody = {
  name: string;
  referenceCode?: string | null | undefined;
  address: Address;
  metadata?: Record<string, unknown> | undefined;
};
export const CreatePropertyBodySchema = z.object({
  name: z.string().min(1),
  referenceCode: z.string().nullable().optional(),
  address: AddressSchema,
  metadata: z.record(z.unknown()).optional(),
}).strict();

export type UpdatePropertyBody = {
  name?: string | undefined;
  referenceCode?: string | null | undefined;
  address?: Address | undefined;
  metadata?: Record<string, unknown> | undefined;
};
export const UpdatePropertyBodySchema = z.object({
  name: z.string().min(1).optional(),
  referenceCode: z.string().nullable().optional(),
  address: AddressSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type ListPropertiesResponse = {
  items: Array<Property>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListPropertiesQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
});

export async function listProperties(query?: {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
}): Promise<ListPropertiesResponse> {
  const response = await api.get<ListPropertiesResponse>(`/properties`, { params: query });
  return response.data;
}

export type CreatePropertyResponse = {
  data: Property;
};

export async function createProperty(body: CreatePropertyBody): Promise<CreatePropertyResponse> {
  const response = await api.post<CreatePropertyResponse>(`/properties`, body);
  return response.data;
}

export type GetPropertyResponse = {
  data: Property;
};

export const GetPropertyParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function getProperty(id: string): Promise<GetPropertyResponse> {
  const response = await api.get<GetPropertyResponse>(`/properties/${encodeURIComponent(id)}`);
  return response.data;
}

export type UpdatePropertyResponse = {
  data: Property;
};

export const UpdatePropertyParamsSchema = z.object({
  id: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function updateProperty(id: string, body: UpdatePropertyBody): Promise<UpdatePropertyResponse> {
  const response = await api.patch<UpdatePropertyResponse>(`/properties/${encodeURIComponent(id)}`, body);
  return response.data;
}

export const propertiesKeys = {
  all: ["properties"] as const,
  lists: () => [...propertiesKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...propertiesKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...propertiesKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertiesKeys.details(), id] as const,
};
